import {
  useMediaCategoryControllerGetMediaCategoryQuery,
  useMediaCategoryControllerSetMediaCategoryMutation,
  useMediaCategoryControllerClearMediaCategoryMutation,
} from "~/ui-rtk/api/mediaCategoryApi.ts"
import {
  useCustomAttributionControllerGetAllMediaTargetsQuery,
  useCustomAttributionControllerGetInitiativeTypesQuery,
} from "~/ui-rtk/api/customAttributionApi.ts"
import { InitiativeType } from "~/ui-rtk/api/types/custom-attribution-enums"
import { INITIATIVE_TYPE_LABELS } from "~/ui-rtk/constants/initiative-types"
import { renderSourceIcon } from "~/ui-rtk/components/ui/charts/TableVisualization/components/Cell/utils"
import { TSource } from "~/ui-rtk/constants/table-visualization-types"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import { H5 } from "~/ui-rtk/components/ui/typography"
import useSources from "~/ui-rtk/hooks/sources"
import { useStats } from "../hooks/stats"
import BrandValueOverSpendProgressBar from "../components/BrandValueOverSpendProgressBar"
import { useCallback, useEffect, useMemo, useState } from "react"
import { TCampaignAdSetAdDetailsProps } from "../constants"
import { useCampaignQubeQuery } from "./hooks/cube-query"
import StatsTable from "../components/StatsTable"
import { renderDetailsCaption } from "../utils"
import { DetailsTitle } from "../components/DetailsHeader"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { URL_CAMPAIGN_ID_PARAM_KEY } from "~/ui-rtk/constants/url-params"
import { URL_WIDGET_PARAMS } from "../../dashboards/BrandMediaOptimizationDashboard/constants"
import { ArrowTopRightOnSquare } from "~/ui-rtk/components/ui/svg/arrow/ArrowTopRightOnSquare"
import NavigationButton from "~/ui-rtk/components/ui/controls/NavigationButton/NavigationButton"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { InitiativeTypeDropdown } from "../../initiative-type"

const BASE_INITIATIVE_TYPE_OPTIONS = [
  { value: "", label: "Clear Selection" },
  { value: "Exclude", label: "Exclude" },
  {
    value: InitiativeType.DEMAND_CAPTURE_ACQUISITION,
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_ACQUISITION],
  },
  {
    value: InitiativeType.DEMAND_CAPTURE_RETARGETING,
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_RETARGETING],
  },
  {
    value: InitiativeType.DEMAND_CAPTURE_BROAD,
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_BROAD],
  },
  {
    value: InitiativeType.DEMAND_GENERATION,
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_GENERATION],
  },
  {
    value: InitiativeType.DEMAND_GENERATION_2,
    label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_GENERATION_2],
  },
  {
    value: InitiativeType.RETENTION,
    label: INITIATIVE_TYPE_LABELS[InitiativeType.RETENTION],
  },
]

const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation()

export default function CampaignDetails(props: TCampaignAdSetAdDetailsProps) {
  const { sources } = useSources()
  const { metric, viewAdsetsUrl, viewAdsUrl, hideViewAdsets } = props
  const stats = useStats(metric)
  const [category, setCategory] = useState<string | null | undefined>(undefined)
  const campaignName = metric.campaign_name as string
  const campaignId = metric.campaign_id as string
  const mediaChannel = metric.media_channel as string
  const sourceName = mediaChannel?.toUpperCase()
  const source = sources.get(sourceName)
  const { allCampaignsDetails } = useCampaignQubeQuery()

  const { isEnabled: isDemoEnabled } = useDemoMode()

  const [setMediaCategory] =
    useMediaCategoryControllerSetMediaCategoryMutation()
  const [clearMediaCategory] =
    useMediaCategoryControllerClearMediaCategoryMutation()

  const { data: allMediaTargets, refetch: refetchMediaTargets } =
    useCustomAttributionControllerGetAllMediaTargetsQuery()

  const { data: detailedTypes = [] } =
    useCustomAttributionControllerGetInitiativeTypesQuery()
  const initiativeTypeOptions = useMemo(
    () => [
      ...BASE_INITIATIVE_TYPE_OPTIONS,
      ...detailedTypes
        .filter(t => t.isCustom)
        .map(t => ({ value: t.value, label: t.label })),
    ],
    [detailedTypes],
  )

  // Find existing media target for this campaign. Stable as long as the RTK
  // Query cache for allMediaTargets is stable (it is, until invalidated).
  const existingMediaTargetInitiativeType = useMemo(() => {
    const target = (
      allMediaTargets as Record<string, unknown>[] | undefined
    )?.find(t => t.campaign_id === campaignId)
    return (target as Record<string, unknown> | undefined)?.initiativeType as
      | string
      | undefined
  }, [allMediaTargets, campaignId])

  const {
    data: mediaCategory,
    isLoading,
    isFetching,
    refetch,
  } = useMediaCategoryControllerGetMediaCategoryQuery(
    {
      id: campaignId,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    if (existingMediaTargetInitiativeType) {
      setCategory(existingMediaTargetInitiativeType)
    } else if (mediaCategory?.category) {
      setCategory(mediaCategory.category)
    } else {
      setCategory(null)
    }
  }, [existingMediaTargetInitiativeType, mediaCategory])

  const categoryUpdate = useCallback(
    async (value: string) => {
      if (!value) {
        await clearMediaCategory({ type: "campaign", id: campaignId })
        setCategory(null)
        await refetch()
        await refetchMediaTargets()
        return
      }

      await setMediaCategory({
        type: "campaign",
        id: campaignId,
        body: {
          value,
          channel: mediaChannel,
        },
      })

      setCategory(value)
      await refetch()
      await refetchMediaTargets()
    },
    [
      campaignId,
      mediaChannel,
      clearMediaCategory,
      setMediaCategory,
      refetch,
      refetchMediaTargets,
    ],
  )

  const viewChildrenAdditionalUrlParams = useMemo(
    () => `${URL_CAMPAIGN_ID_PARAM_KEY}=${campaignId}`,
    [campaignId],
  )

  return (
    <div className="campaign-details w-full">
      <DetailsTitle
        title={campaignName ?? ""}
        classes={{ container: "mb-4" }}
        demoMode={isDemoEnabled}
      />
      <div>
        {isLoading || isFetching ? (
          <Loader />
        ) : (
          <InitiativeTypeDropdown
            value={category || ""}
            options={initiativeTypeOptions}
            onChange={categoryUpdate}
            onStopPropagation={stopPropagation}
            placeholder="Select Initiative Type or Category..."
          />
        )}
      </div>
      <Card
        rounded
        className="mt-3 bg-basic-dark-blue border border-basic-blue"
      >
        <div className="grid grid-cols-8">
          <div className="col-span-7">
            <H5 className={cn("font-bold", isDemoEnabled && "blur-md")}>
              {campaignName as React.ReactNode}
            </H5>
          </div>
          <div className="flex items-center justify-end">
            {renderSourceIcon({
              name: source?.name,
              icon: source?.icon,
            } as TSource)}
          </div>
        </div>
        <div className="mt-3 text-sm">
          {renderDetailsCaption(
            metric,
            (allCampaignsDetails as Record<string, unknown>) ??
              ({} as Record<string, unknown>),
            "campaign",
            sourceName.toLowerCase() as "meta" | "tiktok",
          )}
        </div>
        <BrandValueOverSpendProgressBar
          metric={metric}
          allDetails={
            (allCampaignsDetails as Record<string, unknown>) ??
            ({} as Record<string, unknown>)
          }
          label="campaign"
        />
      </Card>
      {hideViewAdsets ? (
        <div className="mt-3">
          <NavigationButton
            to={
              viewAdsUrl ||
              `?widget=${URL_WIDGET_PARAMS.AD}&${viewChildrenAdditionalUrlParams}`
            }
          >
            <ArrowTopRightOnSquare fill="white" size={20} /> View Ads
          </NavigationButton>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <NavigationButton
            to={
              viewAdsetsUrl ||
              `?widget=${URL_WIDGET_PARAMS.ADSET}&${viewChildrenAdditionalUrlParams}`
            }
          >
            <ArrowTopRightOnSquare fill="white" size={20} /> View AdSets
          </NavigationButton>
          <NavigationButton
            to={
              viewAdsUrl ||
              `?widget=${URL_WIDGET_PARAMS.AD}&${viewChildrenAdditionalUrlParams}`
            }
          >
            <ArrowTopRightOnSquare fill="white" size={20} /> View Ads
          </NavigationButton>
        </div>
      )}
      <Card
        rounded
        className="mt-3 bg-basic-dark-blue border border-basic-blue"
      >
        <H5 className="font-bold">Stats</H5>
        <StatsTable {...stats} />
      </Card>
    </div>
  )
}
